import React from 'react';
import {
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { PassWalletSummary } from '../../models/Pass';
import type { PassEligibilityEmptyReason } from '../../utils/passEligibility';
import { formatPassValidUntil, passRemainingQuantityLabel } from '../../utils/passFormatting';

export type PassTender = 'money' | 'pass';

interface PassTenderPickerProps {
  eligiblePasses: PassWalletSummary[];
  eligiblePassesLoading?: boolean;
  /** Why the list is empty when there are no eligible passes. */
  emptyReason?: PassEligibilityEmptyReason | null;
  selectedPassId: string | null;
  onSelectPass: (passId: string) => void;
}

/**
 * Eligible-pass list shown when the user picks "Use pass" in the booking payment step.
 * Rendered only while tender is `pass`. XOR `paymentMethod`/`passId` is enforced by the
 * parent stepper (`CreateRoomBookingRequest` / `CreateTutorBookingRequest`).
 */
const PassTenderPicker: React.FC<PassTenderPickerProps> = ({
  eligiblePasses,
  eligiblePassesLoading = false,
  emptyReason = null,
  selectedPassId,
  onSelectPass,
}) => {
  const { t, i18n } = useTranslation();

  if (eligiblePassesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (eligiblePasses.length === 0) {
    const emptyMessage =
      emptyReason === 'insufficient_balance'
        ? t(
            'payment.tender.insufficientBalance',
            'You have a pass for this booking, but it doesn’t have enough time left.'
          )
        : t('payment.tender.noEligiblePasses', "You don't have an eligible pass for this booking.");
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Box>
      <List
        dense
        disablePadding
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}
      >
        {eligiblePasses.map((pass, index) => {
          const validUntilLabel = formatPassValidUntil(pass.validUntil, i18n.language);
          const selected = pass.id === selectedPassId;
          const validitySecondary = validUntilLabel
            ? t('userPasses.validUntil', 'Valid until {{date}}', { date: validUntilLabel })
            : pass.validityDurationDays != null && pass.validityDurationDays > 0
              ? t(
                  'userPasses.startsOnFirstUseWithDays',
                  'Valid for {{count}} days from first use',
                  { count: pass.validityDurationDays }
                )
              : t('userPasses.startsOnFirstUse', 'Starts on first use');
          return (
            <ListItemButton
              key={pass.id}
              selected={selected}
              onClick={() => onSelectPass(pass.id)}
              sx={{
                borderBottom: index < eligiblePasses.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                py: 1,
              }}
            >
              <ListItemText
                primary={pass.title}
                secondary={[
                  t('payment.tender.coversThisBooking', 'Covers this booking'),
                  passRemainingQuantityLabel(pass.remainingQuantity, pass.totalQuantity, pass.unitType, t),
                  validitySecondary,
                ]
                  .filter(Boolean)
                  .join(' • ')}
              />
              {selected && <CheckCircleIcon color="primary" fontSize="small" />}
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

export default PassTenderPicker;
